import { useCallback, useEffect, useLayoutEffect, useState, type CSSProperties, type RefObject } from 'react';

export type Side = 'top' | 'bottom';
export type Align = 'start' | 'center' | 'end';
export type Placement = `${Side}-${Align}`;

const useIsoLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

type Rect = { top: number; left: number; width: number; height: number };

/**
 * Pure placement maths, exported for tests: where the floating box goes relative to the anchor,
 * flipped to the other side when it would leave the viewport and clamped to the viewport edges.
 */
export function computePosition(
  anchor: Rect,
  floating: { width: number; height: number },
  viewport: { width: number; height: number },
  placement: Placement,
  offset = 8,
  padding = 8,
): { top: number; left: number; placement: Placement } {
  const [side, align] = placement.split('-') as [Side, Align];
  const below = anchor.top + anchor.height + offset;
  const above = anchor.top - offset - floating.height;
  const fitsBelow = below + floating.height <= viewport.height - padding;
  const fitsAbove = above >= padding;
  const finalSide: Side =
    side === 'bottom' ? (fitsBelow || !fitsAbove ? 'bottom' : 'top') : fitsAbove || !fitsBelow ? 'top' : 'bottom';
  const top = finalSide === 'bottom' ? below : above;

  let left =
    align === 'start'
      ? anchor.left
      : align === 'end'
        ? anchor.left + anchor.width - floating.width
        : anchor.left + anchor.width / 2 - floating.width / 2;
  left = Math.min(Math.max(left, padding), Math.max(padding, viewport.width - padding - floating.width));

  return { top, left, placement: `${finalSide}-${align}` };
}

export type UsePositionOptions = { open: boolean; placement?: Placement; offset?: number };

/**
 * Positions a floating element (Tooltip, Menu, popover) next to its anchor with `position: fixed`,
 * flipping and clamping to stay on screen, and following scroll and resize while open. Renders
 * hidden until measured, so it never flashes at 0,0. Server-safe.
 */
export function usePosition(
  anchorRef: RefObject<HTMLElement | null>,
  floatingRef: RefObject<HTMLElement | null>,
  { open, placement = 'bottom-start', offset = 8 }: UsePositionOptions,
): { style: CSSProperties; placement: Placement; update: () => void } {
  const [state, setState] = useState<{ top: number; left: number; placement: Placement } | null>(null);

  const update = useCallback(() => {
    const anchor = anchorRef.current;
    const floating = floatingRef.current;
    if (!anchor || !floating) return;
    const a = anchor.getBoundingClientRect();
    const f = floating.getBoundingClientRect();
    const next = computePosition(a, f, { width: window.innerWidth, height: window.innerHeight }, placement, offset);
    // Only re-render when the box actually moved (callers may call this from a ref callback).
    setState((prev) =>
      prev && prev.top === next.top && prev.left === next.left && prev.placement === next.placement ? prev : next,
    );
  }, [anchorRef, floatingRef, placement, offset]);

  useIsoLayoutEffect(() => {
    if (!open) {
      setState(null);
      return undefined;
    }
    update();
    // A portal mounted in this same commit may not be measurable until the next frame.
    const frame = requestAnimationFrame(update);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, update]);

  return {
    style: state
      ? { position: 'fixed', top: state.top, left: state.left }
      : { position: 'fixed', top: 0, left: 0, visibility: 'hidden' },
    placement: state?.placement ?? placement,
    /** Call from the floating element's ref callback so a late-mounting portal is measured at once. */
    update,
  };
}
